"use client";

/**
 * Section components of ChatCommandMenu: the header, the body with its toggle
 * and action rows, and the footer. Split out of the menu so each piece reads
 * as one small function; the markup is unchanged.
 */

import type { ChatCommandMenuItem, ChatCommandMenuSection } from "./ChatCommandMenu";
import type { CommandMenuView } from "./ChatCommandMenu.sections";

import React from "react";
import { Link, Switch, Tooltip, TooltipTrigger, TooltipContent } from "@heroui/react";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";

import { logger } from "@/telemetry";

interface KbdHintProps {
  children: React.ReactNode;
}

/** Renders the keyboard shortcut badge used in the footer. */
const KbdHint = ({ children }: KbdHintProps) => (
  <kbd className="px-1 py-0.5 bg-default-100 rounded text-[10px] font-mono">{children}</kbd>
);

/** Whether an item is the one the keyboard selection currently points at. */
const isActionActive = (
  actionItems: ChatCommandMenuItem[],
  item: ChatCommandMenuItem,
  selectedIndex: number,
): boolean => {
  const actionIndex = actionItems.findIndex((a) => a.id === item.id);

  return actionIndex !== -1 && actionIndex === selectedIndex;
};

// ---------------------------------------------------------------------------
// Rows
// ---------------------------------------------------------------------------

interface ToggleRowProps {
  item: ChatCommandMenuItem;
}

/** A toggle row: mouse/pointer-first, so it is left out of arrow-key navigation. */
const ToggleRow = ({ item }: ToggleRowProps) => {
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      item.onToggle?.(!item.toggleValue);
    }
  };

  return (
    <div
      aria-checked={item.toggleValue}
      className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-default-100 transition-colors select-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-inset rounded"
      role="menuitemcheckbox"
      tabIndex={0}
      onClick={() => item.onToggle?.(!item.toggleValue)}
      onKeyDown={handleKeyDown}
    >
      {/* Leading icon */}
      <Icon className="w-4 h-4 text-default-500 shrink-0" icon={item.icon} />

      {/* Label */}
      <span className="flex-1 text-sm text-default-700">{item.label}</span>

      {/* Toggle — stopPropagation prevents the row onClick from double-firing */}
      <Switch
        isSelected={item.toggleValue}
        size="sm"
        onChange={(val: boolean) => {
          item.onToggle?.(val);
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Switch.Content>
          <Switch.Control>
            <Switch.Thumb />
          </Switch.Control>
        </Switch.Content>
      </Switch>
    </div>
  );
};

interface ActionRowPartProps {
  item: ChatCommandMenuItem;
}

/**
 * Label — skill rows get /{key} + description; rows with a plain
 * description (e.g. workflows) get name + description; otherwise label.
 */
const ActionRowLabel = ({ item }: ActionRowPartProps) =>
  item.skillKey ? (
    <span className="flex-1 flex flex-col gap-0.5 min-w-0">
      <span className="text-sm font-medium text-default-800 truncate">/{item.skillKey}</span>
      {item.skillDescription && (
        <span className="text-xs text-default-400 truncate">{item.skillDescription}</span>
      )}
    </span>
  ) : item.description ? (
    <span className="flex-1 flex flex-col gap-0.5 min-w-0">
      <span className="text-sm text-default-700 truncate">{item.label}</span>
      <span className="text-xs text-default-400 truncate">{item.description}</span>
    </span>
  ) : (
    <span className="flex-1 text-sm text-default-700">{item.label}</span>
  );

/** Trailing indicator: tick when current, chevron otherwise. */
const ActionRowTrailing = ({ item }: ActionRowPartProps) => {
  const { t } = useTranslation();

  return item.isCurrent ? (
    <Icon
      aria-label={t("chatCommandMenu.workflows.active")}
      className="w-4 h-4 text-success-500 shrink-0"
      icon="solar:check-circle-bold"
    />
  ) : !item.isDisabled ? (
    <Icon className="w-3 h-3 text-default-300 shrink-0" icon="solar:alt-arrow-right-linear" />
  ) : null;
};

/** The row's classes: dimmed when disabled, highlighted when keyboard-selected. */
const actionRowClassName = (item: ChatCommandMenuItem, isActive: boolean): string =>
  [
    "flex items-center gap-3 px-3 py-2 transition-colors select-none",
    item.isDisabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer hover:bg-default-100",
    isActive && !item.isDisabled ? "bg-default-100" : "",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-inset rounded",
  ]
    .filter(Boolean)
    .join(" ");

interface ActionRowProps {
  item: ChatCommandMenuItem;
  isActive: boolean;
  actionItems: ChatCommandMenuItem[];
  setSelectedIndex: (index: number) => void;
  onClose: () => void;
}

/** An action row: activates by click or Enter/Space and joins arrow-key navigation. */
const ActionRow = ({ item, isActive, actionItems, setSelectedIndex, onClose }: ActionRowProps) => {
  const handleClick = () => {
    if (item.isDisabled) return;
    logger.info("ChatCommandMenu: item activated via click", { id: item.id });
    item.onSelect?.();
    if (!item.keepOpenOnSelect) onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      if (item.isDisabled) return;
      item.onSelect?.();
      if (!item.keepOpenOnSelect) onClose();
    }
  };

  const handleMouseEnter = () => {
    if (item.isDisabled) return;
    const idx = actionItems.findIndex((a) => a.id === item.id);

    if (idx !== -1) setSelectedIndex(idx);
  };

  return (
    <div
      aria-disabled={item.isDisabled}
      className={actionRowClassName(item, isActive)}
      data-action-id={item.id}
      role="menuitem"
      tabIndex={-1}
      title={item.isDisabled && item.disabledReason ? item.disabledReason : undefined}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onMouseEnter={handleMouseEnter}
    >
      {/* Leading icon */}
      <Icon className="w-4 h-4 text-default-500 shrink-0" icon={item.icon} />

      <ActionRowLabel item={item} />

      <ActionRowTrailing item={item} />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

interface HeaderBackButtonProps {
  view: CommandMenuView;
  workflowsLabel?: string;
  onBack: () => void;
}

/** The drill-in sub-views' back affordance, titled with the sub-view's name. */
const HeaderBackButton = ({ view, workflowsLabel, onBack }: HeaderBackButtonProps) => {
  const { t } = useTranslation();

  return (
    <button
      aria-label={t("chatCommandMenu.workflows.back")}
      className="flex flex-1 min-w-0 items-center gap-2 px-3 py-2 text-left hover:bg-default-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-inset"
      data-id={view === "workflows" ? "workflows-back" : "skills-back"}
      type="button"
      onClick={onBack}
    >
      <Icon className="w-4 h-4 text-default-500 shrink-0" icon="solar:alt-arrow-left-linear" />
      <span className="text-xs font-semibold text-default-600 truncate">
        {view === "workflows"
          ? workflowsLabel || t("chatCommandMenu.sections.workflows")
          : t("chatCommandMenu.sections.skills")}
      </span>
    </button>
  );
};

/** The main view's title. */
const HeaderTitle = () => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-1 min-w-0 items-center px-3 py-2">
      <span className="text-xs font-semibold text-default-600">{t("chatCommandMenu.header")}</span>
    </div>
  );
};

interface MenuHeaderProps {
  view: CommandMenuView;
  workflowsLabel?: string;
  onBack: () => void;
  onClose: () => void;
}

/**
 * Header — main view shows the title; the Workflows sub-view shows a back
 * affordance. A close (X) sits on the right so touch users can dismiss the
 * menu (Esc is unavailable on mobile).
 */
export const MenuHeader = ({ view, workflowsLabel, onBack, onClose }: MenuHeaderProps) => {
  const { t } = useTranslation();

  return (
    <div className="flex items-stretch border-b border-divider">
      {view !== "main" ? (
        <HeaderBackButton view={view} workflowsLabel={workflowsLabel} onBack={onBack} />
      ) : (
        <HeaderTitle />
      )}

      {/* Close (X) — pointer/touch dismissal */}
      <button
        aria-label={t("Close")}
        className="flex shrink-0 items-center justify-center px-3 min-h-11 text-default-400 hover:text-default-600 hover:bg-default-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-inset"
        data-id="command-menu-close"
        type="button"
        onClick={onClose}
      >
        <Icon className="w-4 h-4" icon="solar:close-circle-linear" />
      </button>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Body
// ---------------------------------------------------------------------------

interface RowSelectionProps {
  actionItems: ChatCommandMenuItem[];
  selectedIndex: number;
  setSelectedIndex: (index: number) => void;
  onClose: () => void;
}

interface SubViewRowsProps extends RowSelectionProps {
  items: ChatCommandMenuItem[];
}

/** The rows of a drill-in sub-view, with a hairline above any row that asks for one. */
const SubViewRows = ({ items, actionItems, selectedIndex, ...rowProps }: SubViewRowsProps) => (
  <>
    {items.map((item) => (
      <React.Fragment key={item.id}>
        {item.dividerBefore && <div className="my-1 h-px bg-divider" />}
        <ActionRow
          actionItems={actionItems}
          isActive={isActionActive(actionItems, item, selectedIndex)}
          item={item}
          {...rowProps}
        />
      </React.Fragment>
    ))}
  </>
);

interface MenuSectionProps extends RowSelectionProps {
  section: ChatCommandMenuSection;
}

/** One main-view section: its label (omitted for headerless drill-in rows) and items. */
const MenuSection = ({ section, actionItems, selectedIndex, ...rowProps }: MenuSectionProps) => {
  // Skip sections that have no visible items.
  if (section.items.length === 0) return null;

  return (
    <div>
      {/* Section label (omitted for headerless drill-in rows) */}
      {section.label && (
        <div className="px-3 pt-2 pb-1">
          <span className="text-tiny font-semibold uppercase tracking-wider text-default-400">
            {section.label}
          </span>
        </div>
      )}

      {/* Section items */}
      {section.items.map((item) =>
        item.isToggle ? (
          <ToggleRow key={item.id} item={item} />
        ) : (
          <ActionRow
            key={item.id}
            actionItems={actionItems}
            isActive={isActionActive(actionItems, item, selectedIndex)}
            item={item}
            {...rowProps}
          />
        ),
      )}
    </div>
  );
};

interface MenuBodyProps extends RowSelectionProps {
  view: CommandMenuView;
  sections: ChatCommandMenuSection[];
  workflowSubItems: ChatCommandMenuItem[];
  skillSubItems: ChatCommandMenuItem[];
}

/** Body — sections (main) or the workflow / skill list (sub-view). */
export const MenuBody = ({
  view,
  sections,
  workflowSubItems,
  skillSubItems,
  ...rowProps
}: MenuBodyProps) => (
  <div className="overflow-y-auto max-h-72">
    {view !== "main" ? (
      <SubViewRows items={view === "workflows" ? workflowSubItems : skillSubItems} {...rowProps} />
    ) : (
      sections.map((section) => <MenuSection key={section.id} section={section} {...rowProps} />)
    )}
  </div>
);

// ---------------------------------------------------------------------------
// Footer
// ---------------------------------------------------------------------------

/** Footer — keyboard hints and the settings link. */
export const MenuFooter = () => {
  const { t } = useTranslation();

  return (
    <div className="px-3 py-2 border-t border-divider text-xs text-default-400 flex items-center justify-between gap-2">
      {/* Keyboard hints */}
      <div className="flex items-center gap-3 shrink-0">
        <span className="flex items-center gap-1">
          <KbdHint>↑↓</KbdHint>
          {t("chatCommandMenu.footer.navigate")}
        </span>
        <span className="flex items-center gap-1">
          <KbdHint>Enter</KbdHint>
          {t("chatCommandMenu.footer.select")}
        </span>
        <span className="flex items-center gap-1">
          <KbdHint>Esc</KbdHint>
          {t("chatCommandMenu.footer.close")}
        </span>
      </div>

      {/* Settings link: icon-only with the label in a tooltip to avoid clipping */}
      <Tooltip>
        <TooltipTrigger>
          <Link
            aria-label={t("chatCommandMenu.footer.manageInSettings")}
            className="text-primary hover:opacity-80 flex items-center shrink-0"
            href="/settings#general"
            size="sm"
          >
            <Icon className="w-4 h-4" icon="solar:settings-linear" />
          </Link>
        </TooltipTrigger>
        <TooltipContent showArrow>{t("chatCommandMenu.footer.manageInSettings")}</TooltipContent>
      </Tooltip>
    </div>
  );
};
